/**
 * One read-from-the-API-on-mount hook, so pages stop hand-rolling the same
 * `loading` / `error` / `useEffect` block (CLAUDE.md §7).
 *
 * The logic worth having in exactly one place is the cancellation: hold the
 * in-flight request, abort it on unmount, and abort the previous one before
 * starting a new load so a slow earlier response cannot land last and overwrite
 * a newer one. Three pages repeated that by hand and had already drifted into
 * three different shapes.
 *
 * `failed` is a flag rather than a message: the caller translates at render, so
 * switching locale re-renders the text instead of re-fetching the data.
 *
 * **`cacheKey` (PIVOT-136) is opt-in stale-while-revalidate**, not a default,
 * because some reads must always be provably fresh — making every caller cache
 * by default would be exactly the "correlates until it doesn't" mistake
 * `CLAUDE.md` warns about. Passing a key primes `data` from a module-level
 * `Map` shared by every `useApiResource` call with that key, so a page that
 * unmounted (a route change) and remounts paints the last render immediately
 * instead of a bare `loading` state — a real fetch still runs underneath it,
 * and `data` still updates when it lands. A failed revalidation leaves the
 * stale `data` on screen and sets `failed`; the caller decides how to show
 * both at once (never blank a real, if old, render — PIVOT-136).
 *
 * The cache lives for the tab's session, not `sessionStorage` — persisting
 * across a reload raises "how old is too old for today's matches", a separate
 * decision PIVOT-136 explicitly punts. It is also never cleared on sign-out:
 * `AuthContext.signOut` does not touch it, so a second account signing in on
 * the same tab can, for one paint, see the previous account's cached page
 * before the revalidation this hook always still runs replaces it. Tracked as
 * a gap rather than folded into this ticket — fixing it means touching
 * `AuthContext`, which this ticket's file list deliberately excludes to stay
 * parallel-safe with sibling tickets touching that area.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { isAbortError } from '../api/client'

export interface ApiResource<T> {
  /** `null` until the first successful load (or a cache hit primes it). */
  data: T | null
  loading: boolean
  /** The last load failed for a reason other than cancellation. */
  failed: boolean
  /** Re-runs the fetch, cancelling whatever is already in flight. */
  reload: () => Promise<void>
  /** For optimistic updates; the next `reload` overwrites it. */
  setData: React.Dispatch<React.SetStateAction<T | null>>
}

export interface UseApiResourceOptions {
  /** Sharing a key across mounts opts into the stale-while-revalidate cache
   * described above. Omit it (the default) for a read that must never show a
   * previous answer. */
  cacheKey?: string
}

/** Module-level: survives the caller unmounting (a route change) so the next
 * mount under the same key can prime from it, but not a page reload. See the
 * module docstring for the sign-out gap this knowingly leaves open. */
const cache = new Map<string, unknown>()

/** Empties the cache. For tests only — `src/test/setup.ts` calls it between
 * cases.
 *
 * The cache outliving one test is the same fact that makes it useful in the
 * app (it outlives an unmount), so a test file that renders the same page
 * twice inherits the previous case's payload and paints it for one frame
 * before its own fetch lands. `SavedSeen.test.tsx`'s saved/dismissed
 * partition made that visible: a row cached under `interested` renders in the
 * *other* section first, so an assertion on the section headings passed alone
 * and failed in a full run, on timing. Isolating every test is the fix; do not
 * call this from application code, which must keep the cache across mounts. */
export function clearApiResourceCache(): void {
  cache.clear()
}

export function useApiResource<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  options?: UseApiResourceOptions,
): ApiResource<T> {
  const { cacheKey } = options ?? {}

  const [data, setData] = useState<T | null>(() =>
    cacheKey !== undefined && cache.has(cacheKey) ? (cache.get(cacheKey) as T) : null,
  )
  // A cache hit still revalidates — this is stale-while-revalidate, not
  // stale-instead-of — so `loading` always starts `true` regardless of the
  // cache. Callers that want to skip the bare loading state render on `data`
  // (present from the cache) rather than gating on `loading`.
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  // Held in refs so `reload` keeps a stable identity even when the caller
  // passes an inline arrow (or, for `cacheKey`, a literal that is nonetheless
  // a fresh value on a caller re-render). Either as a dependency would re-run
  // the mount effect below on every render, which is an infinite fetch loop.
  const fetcherRef = useRef(fetcher)
  const cacheKeyRef = useRef(cacheKey)
  useEffect(() => {
    fetcherRef.current = fetcher
    cacheKeyRef.current = cacheKey
  })

  const inFlight = useRef<AbortController | null>(null)

  const reload = useCallback(async () => {
    inFlight.current?.abort()
    const controller = new AbortController()
    inFlight.current = controller
    setLoading(true)
    setFailed(false)
    try {
      const result = await fetcherRef.current(controller.signal)
      setData(result)
    } catch (reason) {
      // Superseded or unmounted: the intended outcome, not a failure to show.
      if (isAbortError(reason)) return
      // Deliberately does not touch the cache: a failed revalidation must
      // leave whatever was last known-good in place for the next mount, not
      // overwrite it with nothing (PIVOT-136).
      setFailed(true)
    } finally {
      // Only the newest load owns the shared flags; an older one that loses the
      // race must not clear `loading` while its replacement is still running.
      if (inFlight.current === controller) {
        inFlight.current = null
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    void reload()
    return () => inFlight.current?.abort()
  }, [reload])

  // The single place that writes the cache, covering both a `reload` landing
  // and an optimistic `setData` from the caller (`Dashboard`'s interest
  // toggle) — the next mount under this key should start from whichever of
  // those happened last, not only from a completed fetch. A failed `reload`
  // never calls `setData`, so a failure correctly leaves the cache untouched.
  useEffect(() => {
    if (cacheKeyRef.current !== undefined && data !== null) cache.set(cacheKeyRef.current, data)
  }, [data])

  return { data, loading, failed, reload, setData }
}
